/**
 * LogonForm — CAD LOGON form (extracted from CrewLoginPage.js, iter 67).
 *
 * Pure presentational two-column field grid. All state lives in the parent;
 * this component just renders the fields via `leftFields` / `rightFields`
 * (arrays of {label, value, setter, options, readOnly?, isUnit?, hide?}).
 *
 * Validation copy + VAC RCC logon error banner live here too so the parent
 * doesn't need to know the markup.
 */
import React from "react";

const SELECT_CLASS =
  "h-7 w-full rounded-sm border border-zinc-300 bg-white px-1 text-[11px] text-zinc-600 shadow-sm outline-none";

export function LogonForm({
  leftFields,
  rightFields,
  unitId,
  canSubmit,
  isSupervisor,
  logonError,
}) {
  return (
    <div className="flex flex-col items-center justify-start h-full">
      <div className="mb-2 mt-2 text-[20px] font-bold tracking-tight text-white self-start px-6">CADLOGON</div>
      <div className="mt-8 grid max-w-[800px] w-full grid-cols-2 gap-x-6 gap-y-1 px-6">
        {/* LEFT column — agency / borough / crew 1-4 */}
        <div className="space-y-1">
          {leftFields.filter((f) => !f.hide).map((field) => (
            <div key={field.label} className="flex items-center gap-2">
              <label className="w-[220px] text-right text-[16px] font-bold tracking-tight">{field.label} :</label>
              <div className="relative flex-1 min-w-[220px]">
                {field.readOnly ? (
                  <div className={`${SELECT_CLASS} flex items-center bg-gray-100 text-zinc-500`}>
                    {field.value || "---"}
                  </div>
                ) : (
                  <select
                    data-testid={`crew-${field.label.toLowerCase().replace(/ /g, "")}`}
                    className={`${SELECT_CLASS} w-full`}
                    value={field.value}
                    onChange={(e) => field.setter(e.target.value)}
                  >
                    <option value="">Select {field.label}</option>
                    {field.options.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* RIGHT column — unit / tour / radios 1-4 */}
        <div className="space-y-1">
          {rightFields.filter((f) => !f.hide).map((field) => (
            <div key={field.label} className="flex items-center gap-2">
              <label className="w-[120px] text-right text-[16px] font-bold tracking-tight">{field.label} :</label>
              {field.readOnly ? (
                <div
                  data-testid={`crew-${field.label.toLowerCase().replace(/ /g, "")}`}
                  className={`${SELECT_CLASS} flex items-center bg-gray-100 text-zinc-500`}
                >
                  {field.value || "---"}
                </div>
              ) : (
                <select
                  data-testid={`crew-${field.label.toLowerCase().replace(/ /g, "")}`}
                  className={`${SELECT_CLASS} w-full`}
                  value={field.value}
                  onChange={(e) => field.setter(e.target.value)}
                >
                  <option value="">Select {field.label}</option>
                  {field.isUnit
                    ? field.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)
                    : field.options.map((o) => <option key={o} value={o}>{o}</option>)
                  }
                </select>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Minimum-crew/radio validation message — fires once a unit is picked
          but the required headcount isn't met. Supervisor units need 1+1;
          all other unit types need 2+2. */}
      {unitId && !canSubmit && (
        <div
          className="mt-4 text-red-500 text-[14px] font-bold"
          style={{ fontFamily: '"Courier New", monospace' }}
        >
          {isSupervisor ? "MINIMUM 1 CREW MEMBER AND 1 RADIO REQUIRED" : "MINIMUM 2 CREW MEMBERS AND 2 RADIOS REQUIRED"}
        </div>
      )}

      {/* VAC RCC logon rejection banner (e.g. "UNIT ALREADY ON DUTY",
          "CHAUFFEUR NOT AN AUTHORIZED DRIVER"). Distinct styling so it's
          unmistakably an operational error vs. a local form warning. */}
      {logonError && (
        <div
          data-testid="logon-error"
          className="mt-4 mx-6 max-w-[800px] text-red-500 text-[14px] font-bold px-4 py-2 border border-red-500 bg-[#1a0000]"
          style={{ fontFamily: '"Courier New", monospace', whiteSpace: "normal" }}
        >
          {logonError.toUpperCase()}
        </div>
      )}
    </div>
  );
}

export default LogonForm;
